import { Box, Text, VStack, useDisclosure } from '@chakra-ui/react';

import { useLEAccordionItemContext } from '../../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import PhotoLightbox from '../photos/components/PhotoLightbox';
import {
  ADD_MORE_LATER_COPY,
  PHOTO_INPUT_ACCEPT,
  PRELIST_PHOTO_SLOT,
  PRELIST_PHOTO_SLOT_ID,
} from '../photos/constants';
import { usePhotoUpload } from '../photos/hooks/usePhotoUpload';
import PrelistCaptureColumn from './PrelistCaptureColumn';
import PrelistSubmitFooter from './PrelistSubmitFooter';
import usePrelistStepStatus from './usePrelistStepStatus';

const PROMPT_COPY =
  "Add one photo of your car to list. That's all we need to start getting you offers.";
const ANGLE_HINT_COPY = 'Snap the front-left corner of your car so buyers get a clear first look.';
const CONFIRMATION_COPY = "Got it, you're good to go.";

type MarketplacePrelistPhotoStepProps = {
  dealId: string;
  isLast: boolean;
};

// Control arm: a single front-left photo gates listing.
const MarketplacePrelistPhotoStep = ({ dealId, isLast }: MarketplacePrelistPhotoStepProps) => {
  const { setOpenAccordionIndex, index } = useLEAccordionItemContext();
  const {
    photos,
    processingSlotIds,
    fileInputRef,
    handleSlotClick,
    handleFileSelected,
    handleFileDrop,
    handleDelete,
    handleReplace,
  } = usePhotoUpload(dealId, { enforceMinimumPhoto: false });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const photo = photos.get(PRELIST_PHOTO_SLOT_ID);
  const isComplete = photo != null;
  usePrelistStepStatus(dealId, isComplete);

  const handleRemove = async () => {
    await handleDelete(PRELIST_PHOTO_SLOT_ID);
    setOpenAccordionIndex(index);
  };

  return (
    <VStack spacing={5} align="stretch">
      <Text textAlign="center">{PROMPT_COPY}</Text>

      <Box maxW={{ base: '115px', md: '200px' }} mx="auto" w="full">
        <PrelistCaptureColumn
          slot={PRELIST_PHOTO_SLOT}
          photo={photo}
          isProcessing={processingSlotIds.has(PRELIST_PHOTO_SLOT_ID)}
          onClick={() => handleSlotClick(PRELIST_PHOTO_SLOT_ID)}
          onView={onOpen}
          onDelete={handleRemove}
          onFileDrop={(file) => handleFileDrop(PRELIST_PHOTO_SLOT_ID, file)}
        />
      </Box>

      <Text fontSize="sm" color={photo ? 'leaseEndGreen' : 'grayLight'} textAlign="center">
        {photo ? CONFIRMATION_COPY : ANGLE_HINT_COPY}
      </Text>
      <Text fontSize="xs" color="grayLight" textAlign="center">
        {ADD_MORE_LATER_COPY}
      </Text>

      <PrelistSubmitFooter
        dealId={dealId}
        isLast={isLast}
        isComplete={isComplete}
        isBusy={processingSlotIds.size > 0}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_INPUT_ACCEPT}
        hidden
        onChange={handleFileSelected}
      />

      <PhotoLightbox
        isOpen={isOpen}
        onClose={onClose}
        photo={photo}
        slotLabel={PRELIST_PHOTO_SLOT.label}
        onReplace={() => handleReplace(PRELIST_PHOTO_SLOT_ID)}
        onDelete={handleRemove}
      />
    </VStack>
  );
};

export default MarketplacePrelistPhotoStep;
